import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { CashUpSection } from './CashUp';
import { MaintenanceSubmitSection } from './Maintenance';
import { FridgeTempsSection } from './FridgeTemps';
import {
  ArrowRight,
  Sun, Coffee, Moon, Wallet,
  X, Wrench, CheckCircle, Circle,
  AlertTriangle, Edit2, Thermometer, RefreshCw,
  Bell, MessageSquare, Send, Check, Sparkles, Bug,
} from 'lucide-react';

const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
  purple:'#8B7EC8', purpleLight:'#F0EEF9',
};

const SECTION_THEME = {
  opening:           { bg: '#FDF3E7',      active: '#E8923A', text: C.white, icon: Sun,          label: 'Opening'    },
  'mid-shift':       { bg: C.skyBlueLight, active: C.skyText, text: C.white, icon: Coffee,       label: 'Mid-Shift'  },
  'mid-shift-bar':   { bg: C.skyBlueLight, active: C.skyText, text: C.white, icon: Coffee,       label: 'Mid-Shift'  },
  'mid-shift-floor': { bg: C.skyBlueLight, active: C.skyText, text: C.white, icon: Coffee,       label: 'Mid-Shift'  },
  closing:           { bg: '#EEF1F5',      active: C.navy,    text: C.white, icon: Moon,         label: 'Closing'    },
  'closing-bar':     { bg: '#EEF1F5',      active: C.navy,    text: C.white, icon: Moon,         label: 'Closing'    },
  'closing-floor':   { bg: '#EEF1F5',      active: C.navy,    text: C.white, icon: Moon,         label: 'Closing'    },
  cleaning:          { bg: C.purpleLight,  active: C.purple,  text: C.white, icon: Sparkles,     label: 'Cleaning'   },
  fridgetemps:       { bg: C.orangeLight,  active: C.orange,  text: C.white, icon: Thermometer,  label: 'Fridge Temps'},
  cashup:            { bg: C.greenLight,   active: C.green,   text: C.white, icon: Wallet,       label: 'Cash Up'    },
};

const CHECKLIST_SHIFTS   = ['opening', 'mid-shift', 'closing'];
const MID_SUB_SHIFTS     = ['mid-shift-bar', 'mid-shift-floor'];
const CLOSING_SUB_SHIFTS = ['closing-bar', 'closing-floor'];
// Venues without mid-shift (brunch-only / single-service)
const NO_MIDSHIFT_VENUES = ['Box Plantage'];

const DAYS       = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TODAY      = DAYS[new Date().getDay()];
function getToday() { return new Date().toISOString().split('T')[0]; }
const SHIFT_LABELS = { opening: 'Opening', 'mid-shift': 'Mid-Shift', closing: 'Closing' };
const PEST_CHECK_VENUES = ['Box Jordaan', 'Box Plantage', 'Prep Kitchen'];

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function isItemActiveToday(item) {
  if (!item.days) return true;
  return item.days.includes(TODAY);
}

// ─── SVG arc progress ─────────────────────────────────────────────────────────
function CircleArc({ pct, color, size = 36, stroke = 3.5 }) {
  const r    = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = Math.min(pct, 1) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${color}28`} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.4s ease' }} />
      {pct >= 1 && <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke * 0.4} opacity={0.3} />}
    </svg>
  );
}

// ─── Section pill ─────────────────────────────────────────────────────────────
function SectionPill({ sectionKey, isActive, onClick, pct, status, showArc }) {
  const theme     = SECTION_THEME[sectionKey];
  const Icon      = theme.icon;
  const isPartial = status === 'partial';
  const arcColor  = isActive ? theme.text : theme.active;
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: '0.22rem', padding: '0.6rem 0.45rem', borderRadius: '14px', flex: 1, minWidth: '60px',
      border: `2px solid ${isActive ? theme.active : C.border}`,
      background: isActive ? theme.active : C.white,
      color: isActive ? theme.text : C.mid,
      cursor: 'pointer', position: 'relative',
      boxShadow: isActive ? `0 3px 10px ${theme.active}44` : '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.18s',
    }}>
      {showArc && typeof pct === 'number' ? (
        <div style={{ position: 'relative', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircleArc pct={pct} color={arcColor} size={34} stroke={3} />
          <div style={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={12} strokeWidth={2} />
          </div>
        </div>
      ) : (
        <Icon size={17} strokeWidth={isActive ? 2.2 : 1.8} />
      )}
      <span style={{ fontSize: '0.66rem', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
        {theme.label}
      </span>
      {isPartial && !isActive && (
        <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: C.amber, border: `1.5px solid ${C.white}` }} />
      )}
    </button>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    complete:    { label: 'Complete',    color: C.green,   bg: C.greenLight   },
    partial:     { label: 'Partial',     color: C.amber,   bg: C.amberLight   },
    in_progress: { label: 'In Progress', color: C.skyText, bg: C.skyBlueLight },
  };
  const s = map[status];
  if (!s) return null;
  return <span style={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, background: s.bg, padding: '0.15rem 0.5rem', borderRadius: '999px' }}>{s.label}</span>;
}

// ─── Checklist complete popup ─────────────────────────────────────────────────
function ChecklistCompletePopup({ shiftLabel, onClose }) {
  const [visible, setVisible] = useState(false);
  const [lemons, setLemons]   = useState([]);
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 30);
    const generated = Array.from({ length: 8 }, (_, i) => ({
      id: i, left: 10 + Math.random() * 80,
      delay: Math.random() * 0.6, size: 1.2 + Math.random() * 1.2,
      duration: 1.2 + Math.random() * 0.8,
    }));
    setLemons(generated);
    const t2 = setTimeout(() => { setVisible(false); setTimeout(onClose, 300); }, 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
      <style>{`
        @keyframes lemonDrop { 0%{transform:translateY(-60px) rotate(0deg);opacity:1}80%{opacity:1}100%{transform:translateY(100vh) rotate(360deg);opacity:0} }
        @keyframes popIn { 0%{transform:scale(0.6);opacity:0}70%{transform:scale(1.08)}100%{transform:scale(1);opacity:1} }
        @keyframes popOut { 0%{transform:scale(1);opacity:1}100%{transform:scale(0.8);opacity:0} }
      `}</style>
      {lemons.map(l => (
        <div key={l.id} style={{ position: 'fixed', top: '-40px', left: `${l.left}%`, fontSize: `${l.size}rem`, animation: `lemonDrop ${l.duration}s ease-in ${l.delay}s forwards`, pointerEvents: 'none', zIndex: 3001 }}>🍋</div>
      ))}
      <div style={{ background: C.green, borderRadius: '22px', padding: '2rem 2.5rem', textAlign: 'center', boxShadow: '0 16px 48px rgba(0,0,0,0.25)', animation: visible ? 'popIn 0.35s ease forwards' : 'popOut 0.3s ease forwards', pointerEvents: 'auto', zIndex: 3002, minWidth: '260px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🍋</div>
        <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.4rem', fontWeight: 800, color: C.white }}>Checklist complete!</h2>
        <p style={{ margin: 0, fontSize: '0.9rem', color: `${C.white}cc`, fontWeight: 500 }}>{shiftLabel} shift done ✓</p>
      </div>
    </div>
  );
}

// ─── Shift notes banner ───────────────────────────────────────────────────────
function ShiftNotesBanner({ venue, currentShift, currentUser, department }) {
  const [notes, setNotes] = useState([]);
  const load = useCallback(async () => {
    const shiftValues = currentShift === 'mid-shift'
      ? ['mid-shift', 'mid-shift-bar', 'mid-shift-floor']
      : [currentShift];
    const { data } = await supabase
      .from('checklist_shift_notes').select('*')
      .eq('venue', venue).in('to_shift', shiftValues)
      .eq('department', department).eq('dismissed', false)
      .order('created_at', { ascending: true });
    setNotes(data || []);
  }, [venue, currentShift, department]);
  useEffect(() => { load(); }, [load]);
  async function dismiss(id) {
    await supabase.from('checklist_shift_notes').update({
      dismissed: true, dismissed_by: currentUser?.name || null, dismissed_at: new Date().toISOString(),
    }).eq('id', id);
    setNotes(prev => prev.filter(n => n.id !== id));
  }
  if (notes.length === 0) return null;
  return (
    <>
      {notes.map(note => (
        <div key={note.id} style={{ padding: '0.65rem 0.9rem', background: C.greenLight, border: `1px solid ${C.green}44`, borderLeft: `3px solid ${C.green}`, borderRadius: '9px', marginBottom: '0.5rem', display: 'flex', gap: '0.65rem', alignItems: 'flex-start' }}>
          <Bell size={14} style={{ color: C.green, flexShrink: 0, marginTop: '0.12rem' }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 0.18rem', fontSize: '0.7rem', fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Note from {SHIFT_LABELS[note.from_shift] || note.from_shift}
              {note.created_by ? ` · ${note.created_by}` : ''}
              {note.date !== getToday() ? ` · ${new Date(note.date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
            </p>
            <p style={{ margin: 0, fontSize: '0.84rem', color: C.charcoal, lineHeight: 1.5 }}>{note.note}</p>
          </div>
          <button onClick={() => dismiss(note.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.6rem', background: C.white, border: `1px solid ${C.green}55`, borderRadius: '7px', color: C.green, fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer', flexShrink: 0 }}>
            <Check size={10} /> Got it
          </button>
        </div>
      ))}
    </>
  );
}

// ─── Send shift note form ─────────────────────────────────────────────────────
function SendShiftNoteForm({ venue, currentShift, currentUser, department }) {
  const [open,    setOpen]    = useState(false);
  const [toShift, setToShift] = useState('');
  const [text,    setText]    = useState('');
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  useEffect(() => {
    if (currentShift === 'opening')   setToShift('mid-shift');
    if (currentShift === 'mid-shift') setToShift('closing');
    if (currentShift === 'closing')   setToShift('opening');
  }, [currentShift]);
  async function handleSend() {
    if (!text.trim() || !toShift) return;
    setSending(true);
    await supabase.from('checklist_shift_notes').insert({
      venue, department, from_shift: currentShift, to_shift: toShift,
      note: text.trim(), date: getToday(), created_by: currentUser?.name || null, dismissed: false,
    });
    setSending(false); setSent(true); setText(''); setOpen(false);
    setTimeout(() => setSent(false), 3000);
  }
  const otherShifts = CHECKLIST_SHIFTS.filter(s => s !== currentShift);
  if (sent) return (
    <div style={{ padding: '0.36rem 0.75rem', background: C.greenLight, border: `1px solid ${C.green}44`, borderRadius: '8px', fontSize: '0.76rem', color: C.green, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
      <Check size={11} /> Note sent ✓
    </div>
  );
  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.36rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontSize: '0.76rem', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
      <MessageSquare size={12} /> Leave note for next shift
    </button>
  );
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '0.9rem', minWidth: '260px', flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: C.charcoal }}>Leave a note</span>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={13} /></button>
      </div>
      <div style={{ marginBottom: '0.55rem' }}>
        <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>Send to</label>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {otherShifts.map(s => (
            <button key={s} onClick={() => setToShift(s)} style={{ flex: 1, padding: '0.33rem 0', borderRadius: '7px', fontSize: '0.78rem', fontWeight: toShift === s ? 700 : 400, cursor: 'pointer', border: `2px solid ${toShift === s ? C.navy : C.border}`, background: toShift === s ? '#eef1f5' : C.white, color: toShift === s ? C.navy : C.mid }}>
              {SHIFT_LABELS[s]}
            </button>
          ))}
        </div>
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder="What does the next shift need to know?" rows={3}
        style={{ width: '100%', padding: '0.42rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', color: C.charcoal, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '0.5rem' }} />
      <div style={{ display: 'flex', gap: '0.45rem' }}>
        <button onClick={handleSend} disabled={!text.trim() || !toShift || sending} style={{ flex: 1, padding: '0.46rem', borderRadius: '8px', background: text.trim() && toShift ? C.navy : C.border, color: C.white, border: 'none', fontWeight: 600, fontSize: '0.82rem', cursor: text.trim() && toShift ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
          <Send size={12} /> {sending ? 'Sending…' : 'Send Note'}
        </button>
        <button onClick={() => setOpen(false)} style={{ padding: '0.46rem 0.9rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Progress sidebar ─────────────────────────────────────────────────────────
function ProgressSidebar({ items }) {
  return (
    <div style={{ position: 'sticky', top: '1.5rem', width: '200px', flexShrink: 0, background: C.white, borderRadius: '14px', padding: '1rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', alignSelf: 'flex-start' }}>
      <p style={{ margin: '0 0 0.85rem', fontSize: '0.68rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today's Progress</p>
      {items.map(item => (
        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.7rem' }}>
          <div style={{ flexShrink: 0 }}>
            {item.status === 'complete'     ? <CheckCircle size={16} style={{ color: C.green }} />
            : item.status === 'partial'     ? <CheckCircle size={16} style={{ color: C.amber }} />
            : item.status === 'in_progress' ? <Circle size={16} style={{ color: C.orange, opacity: 0.6 }} />
            : item.status === 'alert'       ? <AlertTriangle size={16} style={{ color: C.green }} />
            : <Circle size={16} style={{ color: C.border }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.76rem', fontWeight: 600, color: C.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</p>
            {item.sublabel && <p style={{ margin: 0, fontSize: '0.68rem', color: C.mid }}>{item.sublabel}</p>}
          </div>
          {item.pct !== undefined && (
            <div style={{ flexShrink: 0 }}>
              <CircleArc pct={item.pct} color={item.status === 'complete' ? C.green : C.orange} size={24} stroke={2.5} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Bar/Floor sub-toggle ─────────────────────────────────────────────────────
function SubShiftToggle({ value, onChange, activeColor }) {
  const color = activeColor || C.skyText;
  return (
    <div style={{ display: 'flex', gap: '0.3rem', background: `${color}18`, borderRadius: '8px', padding: '0.2rem' }}>
      {[
        { key: value.replace(/(bar|floor)$/, 'bar'),   label: 'Bar'   },
        { key: value.replace(/(bar|floor)$/, 'floor'), label: 'Floor' },
      ].map(({ key, label }) => {
        const active = value === key;
        return (
          <button key={key} onClick={() => onChange(key)} style={{
            padding: '0.25rem 0.75rem', borderRadius: '6px', border: 'none',
            background: active ? color : 'transparent', color: active ? C.white : color,
            fontSize: '0.78rem', fontWeight: active ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s',
          }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Last saved label ─────────────────────────────────────────────────────────
function LastSavedLabel({ lastSavedAt, isSaving }) {
  if (isSaving) return <span style={{ fontSize: '0.7rem', color: C.mid, fontStyle: 'italic' }}>Saving…</span>;
  if (!lastSavedAt) return null;
  return (
    <span style={{ fontSize: '0.7rem', color: C.mid }}>
      Last checked at <strong style={{ color: C.charcoal, fontWeight: 600 }}>{formatTime(lastSavedAt)}</strong>
    </span>
  );
}

// ─── Checklist section ────────────────────────────────────────────────────────
// NO key prop — persists across tab switches.
// logEntry prop change drives all state sync via useEffect.
function ChecklistSection({
  shift, resolvedShift, subShift, onSubShiftChange,
  venue, templates, logEntry,
  onSave, onUnfinalise,
  isAdmin, currentUser, department,
  initials, onInitialsChange,
}) {
  const theme         = SECTION_THEME[shift] || SECTION_THEME['opening'];
  const items         = templates[resolvedShift] || [];
  const activeItems   = items.filter(i => isItemActiveToday(i));
  const inactiveItems = items.filter(i => !isItemActiveToday(i));

  const isMidShift  = shift === 'mid-shift';
  const isClosing   = shift === 'closing';
  const hasSubShift = isMidShift || isClosing;
  const isManager   = ['admin', 'manager'].includes(currentUser?.role || '');

  const [isFinalised,       setIsFinalised]       = useState(false);
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [itemState,         setItemState]         = useState({});
  const [notes,             setNotes]             = useState('');
  const [isSaving,          setIsSaving]          = useState(false);
  const [lastSavedAt,       setLastSavedAt]       = useState(null);
  const initialsRef    = useRef(null);
  const saveInFlight   = useRef(false);

  // Refs so async persistSave always reads the latest values, not stale closures
  const itemStateRef   = useRef(itemState);
  const notesRef       = useRef(notes);
  const resolvedShiftRef = useRef(resolvedShift);
  useEffect(() => { itemStateRef.current = itemState; },   [itemState]);
  useEffect(() => { notesRef.current = notes; },           [notes]);
  useEffect(() => { resolvedShiftRef.current = resolvedShift; }, [resolvedShift]);

  // ── Sync from logEntry ────────────────────────────────────────────────────
  useEffect(() => {
    const finalised = !!(logEntry && (logEntry.status === 'complete' || logEntry.status === 'partial'));
    setIsFinalised(finalised);
    if (logEntry?.items) {
      const map = {};
      logEntry.items.forEach(i => { map[i.id] = { initials: i.initials || null, initialled_at: i.initialled_at || null }; });
      setItemState(map);
      setNotes(logEntry.notes || '');
      const times = logEntry.items.map(i => i.initialled_at).filter(Boolean);
      if (times.length > 0) setLastSavedAt(times.sort().reverse()[0]);
    } else {
      setItemState({});
      setNotes('');
      setLastSavedAt(null);
    }
  }, [logEntry]); // eslint-disable-line

  // ── Persist save — immediate, no debounce ─────────────────────────────────
  async function persistSave(newItemState, currentNotes) {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setIsSaving(true);

    const built   = activeItems.map(i => ({
      ...i,
      initials:      newItemState[i.id]?.initials      || null,
      initialled_at: newItemState[i.id]?.initialled_at || null,
    }));
    const allDone = built.every(i => i.initials);
    const status  = allDone ? 'complete' : 'in_progress';
    const payload = { items: built, notes: currentNotes, status };
    if (allDone) {
      payload.submitted_at = new Date().toISOString();
      payload.finalised_by = currentUser?.name || null;
    }

    await onSave({ shift: resolvedShiftRef.current, ...payload });

    saveInFlight.current = false;
    setIsSaving(false);
    setLastSavedAt(new Date().toISOString());

    if (allDone) {
      setIsFinalised(true);
      if (!isManager) setShowCompletePopup(true);
    }
  }

  // ── Item tap — fire save immediately with new state ───────────────────────
  function handleItemClick(item) {
    if (isFinalised || !initials.trim()) return;
    const now = new Date().toISOString();
    setItemState(prev => {
      const cur      = prev[item.id];
      const newState = cur?.initials
        ? { ...prev, [item.id]: { initials: null, initialled_at: null } }
        : { ...prev, [item.id]: { initials: initials.trim().toUpperCase(), initialled_at: now } };
      persistSave(newState, notesRef.current);
      return newState;
    });
  }

  async function handleUnfinalise() {
    await onUnfinalise(resolvedShift);
    setIsFinalised(false);
    setTimeout(() => { if (initialsRef.current) initialsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50);
  }

  const doneCount      = activeItems.filter(i => itemState[i.id]?.initials).length;
  const shiftLabel     = shift === 'mid-shift' ? 'Mid-Shift' : shift === 'cleaning' ? 'Cleaning' : shift.charAt(0).toUpperCase() + shift.slice(1);
  const badgeTextColor = theme.text;

  return (
    <div style={{ background: theme.bg, borderRadius: '14px', padding: '1.25rem' }}>
      {showCompletePopup && <ChecklistCompletePopup shiftLabel={shiftLabel} onClose={() => setShowCompletePopup(false)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: C.charcoal }}>{shiftLabel} Checklist</span>
          <StatusBadge status={logEntry?.status} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {hasSubShift && <SubShiftToggle value={subShift} onChange={onSubShiftChange} activeColor={theme.active} />}
          {isFinalised && isAdmin && (
            <button onClick={handleUnfinalise} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.65rem', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.72rem', cursor: 'pointer' }}>
              <Edit2 size={11} /> Amend
            </button>
          )}
        </div>
      </div>

      {shift !== 'cleaning' && (
        <ShiftNotesBanner venue={venue} currentShift={shift} currentUser={currentUser} department={department} />
      )}

      {isFinalised && logEntry && (
        <div style={{ padding: '0.65rem 0.9rem', background: logEntry.status === 'complete' ? C.greenLight : C.amberLight, border: `1px solid ${logEntry.status === 'complete' ? C.green : C.orange}55`, borderRadius: '8px', marginBottom: '1rem', fontSize: '0.82rem', color: logEntry.status === 'complete' ? C.green : C.amber, fontWeight: 500 }}>
          {logEntry.status === 'complete' ? '✓ All items complete' : `${doneCount}/${activeItems.length} items done`}
          {logEntry.finalised_by && <span style={{ fontWeight: 400, marginLeft: '0.4rem' }}>— {logEntry.finalised_by}</span>}
          {logEntry.notes && <div style={{ fontWeight: 400, marginTop: '0.2rem', color: C.mid, fontSize: '0.79rem' }}>{logEntry.notes}</div>}
        </div>
      )}

      {!isFinalised && (
        <div ref={initialsRef} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '1rem', padding: '0.7rem 1rem', background: C.white, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.79rem', color: C.mid, fontWeight: 500, whiteSpace: 'nowrap' }}>Initials</label>
          <input
            value={initials}
            onChange={e => onInitialsChange(e.target.value.toUpperCase().slice(0, 4))}
            placeholder='e.g. EG'
            maxLength={4}
            style={{ width: '58px', padding: '0.38rem 0.45rem', border: `2px solid ${initials ? theme.active : C.border}`, borderRadius: '8px', fontSize: '0.95rem', fontWeight: 800, color: C.charcoal, textAlign: 'center', letterSpacing: '0.05em', background: initials ? C.white : C.cream, outline: 'none' }}
          />
          <span style={{ fontSize: '0.74rem', color: C.mid, flex: 1 }}>
            {!initials ? 'Enter initials to start' : 'Tap to mark · tap again to undo'}
          </span>
          <LastSavedLabel lastSavedAt={lastSavedAt} isSaving={isSaving} />
        </div>
      )}

      {!isFinalised && shift !== 'cleaning' && (
        <div style={{ marginBottom: '1rem' }}>
          <SendShiftNoteForm venue={venue} currentShift={shift} currentUser={currentUser} department={department} />
        </div>
      )}

      {!isFinalised && activeItems.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <div style={{ flex: 1, height: '4px', borderRadius: '99px', background: `${theme.active}22`, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(doneCount / activeItems.length) * 100}%`, background: theme.active, borderRadius: '99px', transition: 'width 0.3s ease' }} />
          </div>
          <span style={{ fontSize: '0.72rem', color: C.mid, flexShrink: 0 }}>{doneCount}/{activeItems.length}</span>
        </div>
      )}

      <div style={{ background: C.white, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '1rem' }}>
        {activeItems.length === 0 && <p style={{ padding: '1rem', color: C.mid, fontSize: '0.85rem', margin: 0 }}>No items for today.</p>}
        {activeItems.map((item, idx) => {
          const state  = itemState[item.id];
          const done   = !!state?.initials;
          const isLast = idx === activeItems.length - 1 && inactiveItems.length === 0;
          return (
            <div key={item.id} onClick={() => handleItemClick(item)} style={{ display: 'flex', alignItems: 'center', padding: '0.85rem 1rem', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, gap: '0.75rem', cursor: isFinalised ? 'default' : initials ? 'pointer' : 'not-allowed', background: done ? `${theme.active}14` : C.white, transition: 'background 0.15s' }}>
              <div style={{ width: '40px', height: '27px', borderRadius: '6px', flexShrink: 0, border: `2px solid ${done ? theme.active : C.border}`, background: done ? theme.active : C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                {done && <span style={{ color: badgeTextColor, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.02em' }}>{state.initials}</span>}
              </div>
              <span style={{ flex: 1, fontSize: '0.9rem', color: done ? C.mid : C.charcoal, textDecoration: done ? 'line-through' : 'none', transition: 'all 0.15s' }}>{item.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {done && state.initialled_at && <span style={{ fontSize: '0.66rem', color: C.mid }}>{formatTime(state.initialled_at)}</span>}
                {item.days && <span style={{ fontSize: '0.66rem', color: C.mid, background: C.cream, padding: '0.1rem 0.4rem', borderRadius: '999px' }}>{item.days.join(' / ')}</span>}
              </div>
            </div>
          );
        })}
        {inactiveItems.length > 0 && (
          <>
            <div style={{ padding: '0.35rem 1rem', background: C.cream, borderBottom: `1px solid ${C.border}`, borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontSize: '0.64rem', color: C.mid, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Not required today</span>
            </div>
            {inactiveItems.map((item, idx) => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', padding: '0.7rem 1rem', borderBottom: idx < inactiveItems.length - 1 ? `1px solid ${C.border}` : 'none', gap: '0.75rem', opacity: 0.35 }}>
                <div style={{ width: '40px', height: '27px', borderRadius: '6px', border: `2px solid ${C.border}`, background: C.cream, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.88rem', color: C.mid }}>{item.label}</span>
                {item.days && <span style={{ fontSize: '0.66rem', color: C.mid, background: C.cream, padding: '0.1rem 0.4rem', borderRadius: '999px' }}>{item.days.join(' / ')}</span>}
              </div>
            ))}
          </>
        )}
      </div>

      {!isFinalised && (
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: C.mid, fontWeight: 500, marginBottom: '0.28rem' }}>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder='Any notes for this shift...'
            rows={2}
            onBlur={() => { if (notes !== (logEntry?.notes || '')) persistSave(itemStateRef.current, notes); }}
            style={{ width: '100%', padding: '0.42rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal, background: C.white }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Pest Check Banner ────────────────────────────────────────────────────────
function PestCheckBanner({ venue, department, currentUser }) {
  const [entry,        setEntry]        = useState(null);  // null=loading, false=not done, obj=done
  const [showModal,    setShowModal]    = useState(false);
  const [initials,     setInitials]     = useState('');
  const [saving,       setSaving]       = useState(false);
  const [undoing,      setUndoing]      = useState(false);
  const requiresInitials = currentUser?.requires_initials === true;

  useEffect(() => {
    if (!PEST_CHECK_VENUES.includes(venue)) return;
    supabase.from('pest_check_log')
      .select('id,completed_by,completed_at')
      .eq('venue', venue).eq('department', department).eq('date', getToday())
      .maybeSingle()
      .then(({ data }) => setEntry(data || false));
  }, [venue, department]);

  if (!PEST_CHECK_VENUES.includes(venue)) return null;
  if (entry === null) return null; // loading — render nothing to avoid flash

  async function handleConfirm() {
    if (requiresInitials && !initials.trim()) return;
    setSaving(true);
    const by = requiresInitials ? initials.trim().toUpperCase() : (currentUser?.name || 'Unknown');
    const { data } = await supabase.from('pest_check_log')
      .upsert({ venue, department, date: getToday(), completed_by: by, completed_at: new Date().toISOString() }, { onConflict: 'venue,department,date' })
      .select().single();
    setSaving(false);
    setEntry(data || { completed_by: by, completed_at: new Date().toISOString() });
    setShowModal(false);
    setInitials('');
  }

  async function handleUndo() {
    if (!entry?.id) return;
    setUndoing(true);
    await supabase.from('pest_check_log').delete().eq('id', entry.id);
    setEntry(false);
    setUndoing(false);
  }

  const time = entry ? new Date(entry.completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <>
      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => { setShowModal(false); setInitials(''); }}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '380px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#2F3A42', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Bug size={16} color="#F7F6EF" />
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: C.white }}>Pest Check</span>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <p style={{ margin: '0 0 0.9rem', fontSize: '0.88rem', color: C.charcoal, lineHeight: 1.55 }}>
                Confirm all pest-prone areas have been checked and are clear.
              </p>
              {requiresInitials ? (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Your initials</label>
                  <input
                    value={initials}
                    onChange={e => setInitials(e.target.value.toUpperCase().slice(0, 4))}
                    placeholder="e.g. EG"
                    autoFocus
                    maxLength={4}
                    style={{ width: '80px', padding: '0.45rem 0.55rem', border: `2px solid ${initials ? '#2F3A42' : C.border}`, borderRadius: '8px', fontSize: '1.1rem', fontWeight: 800, textAlign: 'center', color: C.charcoal, outline: 'none', letterSpacing: '0.06em' }}
                  />
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleConfirm}
                  disabled={saving || (requiresInitials && !initials.trim())}
                  style={{ flex: 1, padding: '0.55rem', borderRadius: '9px', background: (requiresInitials && !initials.trim()) ? C.border : C.green, color: C.white, border: 'none', fontWeight: 700, fontSize: '0.88rem', cursor: (requiresInitials && !initials.trim()) ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving…' : '✓ All Clear'}
                </button>
                <button onClick={() => { setShowModal(false); setInitials(''); }}
                  style={{ padding: '0.55rem 1rem', borderRadius: '9px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.85rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner */}
      {!entry ? (
        <button onClick={() => setShowModal(true)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem', background: C.redLight, border: `2px solid ${C.red}`, borderRadius: '12px', cursor: 'pointer', textAlign: 'left', marginBottom: '1rem' }}>
          <Bug size={18} color={C.red} strokeWidth={2} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 800, color: C.red }}>Pest check not completed</div>
            <div style={{ fontSize: '0.75rem', color: C.mid, marginTop: '0.1rem' }}>Tap to confirm all areas checked</div>
          </div>
          <ArrowRight size={15} color={C.red} />
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 1rem', background: C.greenLight, border: `1px solid ${C.green}44`, borderLeft: `3px solid ${C.green}`, borderRadius: '12px', marginBottom: '1rem' }}>
          <CheckCircle size={16} color={C.green} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.84rem', fontWeight: 700, color: C.green }}>Pest check done ✓</div>
            <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: '0.1rem' }}>{entry.completed_by} · {time}</div>
          </div>
          <button onClick={handleUndo} disabled={undoing}
            style={{ fontSize: '0.7rem', color: C.mid, background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '0.2rem 0.55rem', cursor: 'pointer' }}>
            {undoing ? '…' : 'Undo'}
          </button>
        </div>
      )}
    </>
  );
}

// ─── Cleaning Item Modal ──────────────────────────────────────────────────────
function CleaningItemModal({ item, currentUser, onConfirm, onUndo, onClose, sessionDoneIds }) {
  const [initials,   setInitials]   = useState('');
  const [saving,     setSaving]     = useState(false);
  const [undoing,    setUndoing]    = useState(false);
  const requiresInitials = currentUser?.requires_initials === true;
  const alreadyDone = sessionDoneIds.has(item.id);

  async function handleConfirm() {
    if (requiresInitials && !initials.trim()) return;
    setSaving(true);
    const by = requiresInitials ? initials.trim().toUpperCase() : (currentUser?.name || 'Unknown');
    await onConfirm(item, by);
    setSaving(false);
    onClose();
  }

  async function handleUndo() {
    setUndoing(true);
    await onUndo(item);
    setUndoing(false);
    onClose();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '400px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: '#8B7EC8', padding: '1rem 1.25rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: C.white, lineHeight: 1.4 }}>{item.label}</div>
            <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>
              {item.days === null ? 'Never completed' : `Last done ${item.days}d ago`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: '7px', cursor: 'pointer', color: C.white, padding: '4px', display: 'flex', flexShrink: 0 }}><X size={14} /></button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {alreadyDone ? (
            // Already done this session — offer undo
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 0.85rem', background: C.greenLight, borderRadius: '9px', marginBottom: '1rem' }}>
                <CheckCircle size={14} color={C.green} />
                <span style={{ fontSize: '0.84rem', fontWeight: 600, color: C.green }}>Marked complete this session</span>
              </div>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: C.mid }}>Did you tick this by mistake?</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleUndo} disabled={undoing}
                  style={{ flex: 1, padding: '0.5rem', borderRadius: '9px', background: C.redLight, color: C.red, border: `1px solid ${C.red}44`, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                  {undoing ? 'Undoing…' : 'Undo completion'}
                </button>
                <button onClick={onClose}
                  style={{ padding: '0.5rem 1rem', borderRadius: '9px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.85rem', cursor: 'pointer' }}>
                  Keep
                </button>
              </div>
            </>
          ) : (
            // Not yet done — confirm
            <>
              {requiresInitials && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Your initials</label>
                  <input
                    value={initials}
                    onChange={e => setInitials(e.target.value.toUpperCase().slice(0, 4))}
                    placeholder="e.g. EG"
                    autoFocus
                    maxLength={4}
                    style={{ width: '80px', padding: '0.45rem 0.55rem', border: `2px solid ${initials ? '#8B7EC8' : C.border}`, borderRadius: '8px', fontSize: '1.1rem', fontWeight: 800, textAlign: 'center', color: C.charcoal, outline: 'none', letterSpacing: '0.06em' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleConfirm}
                  disabled={saving || (requiresInitials && !initials.trim())}
                  style={{ flex: 1, padding: '0.55rem', borderRadius: '9px', background: (requiresInitials && !initials.trim()) ? C.border : '#8B7EC8', color: C.white, border: 'none', fontWeight: 700, fontSize: '0.88rem', cursor: (requiresInitials && !initials.trim()) ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving…' : '✓ Mark Complete'}
                </button>
                <button onClick={onClose}
                  style={{ padding: '0.55rem 1rem', borderRadius: '9px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.85rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Cleaning Section ─────────────────────────────────────────────────────────
function CleaningSection({ venue, department, currentUser }) {
  const [items,          setItems]          = useState([]);   // cleaning_items rows enriched with days
  const [loading,        setLoading]        = useState(true);
  const [modal,          setModal]          = useState(null); // item obj or null
  const [sessionDoneIds, setSessionDoneIds] = useState(new Set()); // ids completed this session
  const [sessionUndoMap, setSessionUndoMap] = useState({});        // id -> completion row id for undo

  // Load cleaning items + latest completions
  useEffect(() => {
    async function load() {
      const { data: rawItems } = await supabase
        .from('cleaning_items')
        .select('id,label,sort_order')
        .eq('venue', venue)
        .eq('department', department)
        .eq('active', true)
        .order('sort_order');

      if (!rawItems?.length) { setLoading(false); return; }

      const ids = rawItems.map(i => i.id);
      const { data: completions } = await supabase
        .from('cleaning_item_completions')
        .select('item_id,completed_at,completed_by')
        .in('item_id', ids)
        .order('completed_at', { ascending: false });

      // Latest completion per item
      const latestMap = {};
      (completions || []).forEach(c => {
        if (!latestMap[c.item_id]) latestMap[c.item_id] = c;
      });

      // Enrich and sort: never-done first, then most days desc
      const enriched = rawItems.map(item => {
        const latest = latestMap[item.id];
        const days   = latest
          ? Math.floor((Date.now() - new Date(latest.completed_at)) / 86400000)
          : null;
        return { ...item, days, lastBy: latest?.completed_by || null, lastAt: latest?.completed_at || null };
      }).sort((a, b) => {
        const da = a.days === null ? 99999 : a.days;
        const db = b.days === null ? 99999 : b.days;
        return db - da;
      });

      setItems(enriched);
      setLoading(false);
    }
    load();
  }, [venue, department]);

  async function handleConfirm(item, by) {
    const { data } = await supabase
      .from('cleaning_item_completions')
      .insert({ item_id: item.id, venue, department, item_label: item.label, completed_by: by, completed_at: new Date().toISOString() })
      .select().single();

    // Update local state
    const now  = new Date().toISOString();
    const days = 0;
    setItems(prev => {
      const updated = prev.map(i => i.id === item.id ? { ...i, days, lastBy: by, lastAt: now } : i)
        .sort((a, b) => {
          const da = a.days === null ? 99999 : a.days;
          const db = b.days === null ? 99999 : b.days;
          return db - da;
        });
      return updated;
    });
    if (data) {
      setSessionDoneIds(prev => new Set([...prev, item.id]));
      setSessionUndoMap(prev => ({ ...prev, [item.id]: data.id }));
    }
  }

  async function handleUndo(item) {
    const completionId = sessionUndoMap[item.id];
    if (!completionId) return;
    await supabase.from('cleaning_item_completions').delete().eq('id', completionId);

    // Reload this item's latest from DB
    const { data: latest } = await supabase
      .from('cleaning_item_completions')
      .select('completed_at,completed_by')
      .eq('item_id', item.id)
      .order('completed_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const days = latest
      ? Math.floor((Date.now() - new Date(latest.completed_at)) / 86400000)
      : null;

    setItems(prev => {
      const updated = prev.map(i => i.id === item.id
        ? { ...i, days, lastBy: latest?.completed_by || null, lastAt: latest?.completed_at || null }
        : i
      ).sort((a, b) => {
        const da = a.days === null ? 99999 : a.days;
        const db = b.days === null ? 99999 : b.days;
        return db - da;
      });
      return updated;
    });
    setSessionDoneIds(prev => { const s = new Set(prev); s.delete(item.id); return s; });
    setSessionUndoMap(prev => { const m = { ...prev }; delete m[item.id]; return m; });
  }

  // Progress = items done within last 7 days
  const doneThisWeek = items.filter(i => i.days !== null && i.days <= 7).length;
  const total        = items.length;
  const pct          = total > 0 ? doneThisWeek / total : 0;

  if (loading) return <div style={{ padding: '1rem', color: C.mid, fontSize: '0.84rem' }}>Loading…</div>;
  if (!items.length) return <div style={{ padding: '1rem', color: C.mid, fontSize: '0.84rem' }}>No cleaning items set up yet.</div>;

  return (
    <div style={{ background: '#F0EEF9', borderRadius: '14px', padding: '1.25rem' }}>
      {modal && (
        <CleaningItemModal
          item={modal}
          currentUser={currentUser}
          onConfirm={handleConfirm}
          onUndo={handleUndo}
          onClose={() => setModal(null)}
          sessionDoneIds={sessionDoneIds}
        />
      )}

      {/* Header + progress */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: C.charcoal }}>Cleaning Checklist</span>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#8B7EC8', background: '#E8E5F8', padding: '0.15rem 0.55rem', borderRadius: '999px' }}>
            {doneThisWeek}/{total} this week
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <div style={{ flex: 1, height: '5px', borderRadius: '99px', background: '#8B7EC822', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct * 100}%`, background: '#8B7EC8', borderRadius: '99px', transition: 'width 0.4s ease' }} />
        </div>
        <span style={{ fontSize: '0.72rem', color: C.mid, flexShrink: 0 }}>{Math.round(pct * 100)}%</span>
      </div>

      {/* Item list */}
      <div style={{ background: C.white, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {items.map((item, idx) => {
          const isDoneSession = sessionDoneIds.has(item.id);
          const dayColor = item.days === null ? C.mid
            : item.days > 12 ? C.red
            : item.days > 7  ? C.amber
            : C.green;
          const isLast = idx === items.length - 1;

          return (
            <button key={item.id} onClick={() => setModal(item)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.82rem 1rem', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, background: isDoneSession ? `${C.green}10` : C.white, border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s' }}
              onMouseEnter={e => { if (!isDoneSession) e.currentTarget.style.background = C.cream; }}
              onMouseLeave={e => { e.currentTarget.style.background = isDoneSession ? `${C.green}10` : C.white; }}>

              {/* Done indicator */}
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, background: isDoneSession ? C.green : C.cream, border: `2px solid ${isDoneSession ? C.green : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isDoneSession && <Check size={11} color={C.white} strokeWidth={3} />}
              </div>

              {/* Label */}
              <span style={{ flex: 1, fontSize: '0.88rem', color: isDoneSession ? C.mid : C.charcoal, textDecoration: isDoneSession ? 'line-through' : 'none', lineHeight: 1.4 }}>
                {item.label}
              </span>

              {/* Days counter */}
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: isDoneSession ? C.green : dayColor, flexShrink: 0, minWidth: '42px', textAlign: 'right' }}>
                {isDoneSession ? '✓ done' : item.days === null ? 'Never' : `${item.days}d`}
              </span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '0.85rem', marginTop: '0.75rem', fontSize: '0.7rem', color: C.mid, flexWrap: 'wrap' }}>
        <span><span style={{ color: C.green, fontWeight: 700 }}>●</span> ≤6 days</span>
        <span><span style={{ color: C.amber, fontWeight: 700 }}>●</span> 7–12 days</span>
        <span><span style={{ color: C.red,   fontWeight: 700 }}>●</span> 12+ days</span>
        <span><span style={{ color: C.mid,   fontWeight: 700 }}>●</span> Never done</span>
      </div>
    </div>
  );
}

// ─── Recurring task alert ─────────────────────────────────────────────────────
function RecurringAlert({ task, onDone, onDismiss }) {
  const [initialling, setInitialling] = useState(false);
  const [initials,    setInitials]    = useState('');
  const [saving,      setSaving]      = useState(false);
  const today       = new Date(); today.setHours(0,0,0,0);
  const dueDate     = new Date(task.next_due_date + 'T00:00:00');
  const overdueDays = Math.floor((today - dueDate) / 86400000);
  const isOverdue   = overdueDays > 0;
  const urgencyColor = overdueDays > 3 ? C.red : C.green;
  const urgencyBg    = overdueDays > 3 ? C.redLight : C.greenLight;
  async function handleComplete() {
    if (!initials.trim()) return;
    setSaving(true);
    const todayStr   = new Date().toISOString().split('T')[0];
    const nextDue    = new Date(); nextDue.setDate(nextDue.getDate() + task.interval_days);
    const nextDueStr = nextDue.toISOString().split('T')[0];
    await supabase.from('recurring_tasks').update({ last_done_date: todayStr, last_done_by: initials.trim().toUpperCase(), next_due_date: nextDueStr }).eq('id', task.id);
    setSaving(false);
    if (onDone) onDone(task.id, todayStr, nextDueStr, initials.trim().toUpperCase());
  }
  return (
    <div style={{ padding: '0.75rem 1rem', background: urgencyBg, border: `1px solid ${urgencyColor}44`, borderLeft: `3px solid ${urgencyColor}`, borderRadius: '9px', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
      <RefreshCw size={14} style={{ color: urgencyColor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: '120px' }}>
        <span style={{ fontWeight: 700, fontSize: '0.84rem', color: C.charcoal }}>{task.label}</span>
        <span style={{ fontSize: '0.74rem', color: urgencyColor, marginLeft: '0.5rem', fontWeight: 600 }}>{isOverdue ? `${overdueDays}d overdue` : 'Due today'}</span>
        {task.notes && <span style={{ fontSize: '0.73rem', color: C.mid, marginLeft: '0.5rem' }}>{task.notes}</span>}
      </div>
      {!initialling ? (
        <button onClick={() => setInitialling(true)} style={{ padding: '0.32rem 0.85rem', borderRadius: '7px', background: urgencyColor, color: C.white, border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Mark done</button>
      ) : (
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexShrink: 0 }}>
          <input value={initials} onChange={e => setInitials(e.target.value.toUpperCase().slice(0,4))} placeholder='Init.' autoFocus maxLength={4} style={{ width: '52px', padding: '0.3rem 0.4rem', border: `2px solid ${urgencyColor}`, borderRadius: '6px', fontSize: '0.9rem', fontWeight: 800, textAlign: 'center', color: C.charcoal, outline: 'none' }} />
          <button onClick={handleComplete} disabled={!initials.trim() || saving} style={{ padding: '0.3rem 0.75rem', background: initials.trim() ? urgencyColor : C.border, color: C.white, border: 'none', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: initials.trim() ? 'pointer' : 'not-allowed' }}>{saving ? '…' : '✓'}</button>
          <button onClick={() => setInitialling(false)} style={{ padding: '0.3rem 0.5rem', background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px', color: C.mid, fontSize: '0.78rem', cursor: 'pointer' }}><X size={11} /></button>
        </div>
      )}
      <button onClick={() => onDismiss(task.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0.1rem', flexShrink: 0 }}><X size={12} /></button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Checklist({
  currentUser, products = [], requests,
  onChecklistUpdate, isAdmin = false,
  allVenues = null, onVenueChange = null, onNavigate = null,
}) {
  const venue      = currentUser?.venue;
  const userRole   = currentUser?.role || 'staff';
  const department = userRole === 'kitchen' ? 'kitchen' : 'bar';
  const isKitchen  = department === 'kitchen';
  const isManager  = ['admin', 'manager'].includes(userRole);
  const hasMidShift = !NO_MIDSHIFT_VENUES.includes(venue);

  const sections    = isKitchen
    ? ['cleaning', 'fridgetemps']
    : [
        'opening',
        ...(hasMidShift ? ['mid-shift'] : []),
        'closing',
        'cleaning',
        'fridgetemps',
        ...(['bar', 'supervisor', 'manager', 'admin'].includes(userRole) ? ['cashup'] : []),
      ];
  const arcSections = isKitchen ? ['cleaning'] : [
    'opening',
    ...(hasMidShift ? ['mid-shift'] : []),
    'closing',
    'cleaning',
  ];

  const [activeSection,      setActiveSection]      = useState(isKitchen ? 'cleaning' : 'opening');
  const [localLog,           setLocalLog]           = useState([]);
  const [venueTemplates,     setVenueTemplates]     = useState({});
  const [recurringTasks,     setRecurringTasks]     = useState([]);
  const [dismissedRecurring, setDismissedRecurring] = useState(new Set());
  const [showMaintenance,    setShowMaintenance]    = useState(false);
  const [showHistory,        setShowHistory]        = useState(false);
  const [midSubShift,        setMidSubShift]        = useState('mid-shift-bar');
  const [closingSubShift,    setClosingSubShift]    = useState('closing-bar');
  // Cleaning items state (for arc pct on pill)
  const [cleaningItems,      setCleaningItems]      = useState([]);
  const [cleaningDoneWeek,   setCleaningDoneWeek]   = useState(0);

  // Initials at parent level — survive tab switches without remount
  const [initials, setInitials] = useState(() => localStorage.getItem('checklist_initials') || '');
  function handleInitialsChange(val) {
    setInitials(val);
    localStorage.setItem('checklist_initials', val);
  }

  const resolvedShift =
    activeSection === 'mid-shift' ? midSubShift :
    activeSection === 'closing'   ? closingSubShift :
    activeSection;

  useEffect(() => {
    if (!venue) return;
    const midShifts = hasMidShift ? ['mid-shift-bar', 'mid-shift-floor'] : [];
    const shiftsToLoad = isKitchen
      ? []  // cleaning no longer reads from checklist_templates
      : ['opening', ...midShifts, 'closing-bar', 'closing-floor'];
    if (!shiftsToLoad.length) return;
    supabase.from('checklist_templates').select('shift,items')
      .eq('venue', venue).eq('department', department).in('shift', shiftsToLoad)
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(row => { map[row.shift] = row.items || []; });
        setVenueTemplates(map);
      });
  }, [venue, department, isKitchen, hasMidShift]);

  // Load cleaning items + this-week completion count for arc pct
  useEffect(() => {
    if (!venue) return;
    async function loadCleaning() {
      const { data: items } = await supabase
        .from('cleaning_items')
        .select('id')
        .eq('venue', venue).eq('department', department).eq('active', true);
      if (!items?.length) return;
      const ids = items.map(i => i.id);
      const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
      const { data: completions } = await supabase
        .from('cleaning_item_completions')
        .select('item_id,completed_at')
        .in('item_id', ids)
        .gte('completed_at', weekAgo.toISOString())
        .order('completed_at', { ascending: false });
      // Unique items done this week
      const doneIds = new Set((completions || []).map(c => c.item_id));
      setCleaningItems(items);
      setCleaningDoneWeek(doneIds.size);
    }
    loadCleaning();
  }, [venue, department]);

  useEffect(() => {
    if (!venue) return;
    supabase.from('checklist_log').select('*').eq('venue', venue)
      .order('submitted_at', { ascending: false }).limit(60)
      .then(({ data }) => { if (data) setLocalLog(data); });
  }, [venue]);

  useEffect(() => {
    if (!venue) return;
    const today = new Date().toISOString().split('T')[0];
    supabase.from('recurring_tasks').select('*').eq('venue', venue).eq('department', department).eq('active', true)
      .or(`next_due_date.lte.${today},next_due_date.is.null`)
      .then(({ data }) => { if (data) setRecurringTasks(data); });
  }, [venue, department]);

  useEffect(() => {
    if (!venue) return;
    const ch = supabase.channel(`checklist_log_${venue}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_log', filter: `venue=eq.${venue}` }, payload => {
        if (payload.eventType === 'INSERT') setLocalLog(prev => [payload.new, ...prev.filter(e => e.id !== payload.new.id)]);
        else if (payload.eventType === 'UPDATE') setLocalLog(prev => prev.map(e => e.id === payload.new.id ? payload.new : e));
      }).subscribe();
    return () => supabase.removeChannel(ch);
  }, [venue]);

  function getTodayEntry(shiftKey) {
    return localLog.find(e => e.venue === venue && e.shift === shiftKey && e.date === getToday()) || null;
  }

  async function handleSave({ shift, items, notes, status, submitted_at, finalised_by }) {
    const existing = getTodayEntry(shift);
    const payload  = { items, notes, status, ...(submitted_at ? { submitted_at, finalised_by } : {}) };
    if (existing) {
      const { data } = await supabase.from('checklist_log').update(payload).eq('id', existing.id).select().single();
      if (data) { setLocalLog(prev => prev.map(e => e.id === data.id ? data : e)); if (onChecklistUpdate) onChecklistUpdate(data); }
    } else {
      const { data } = await supabase.from('checklist_log').insert({ venue, shift, date: getToday(), department, ...payload }).select().single();
      if (data) { setLocalLog(prev => [data, ...prev]); if (onChecklistUpdate) onChecklistUpdate(data); }
    }
  }

  async function handleUnfinalise(shift) {
    const existing = getTodayEntry(shift);
    if (!existing) return;
    const { data } = await supabase.from('checklist_log')
      .update({ status: 'in_progress', submitted_at: null, finalised_by: null })
      .eq('id', existing.id).select().single();
    if (data) { setLocalLog(prev => prev.map(e => e.id === data.id ? data : e)); if (onChecklistUpdate) onChecklistUpdate(data); }
  }

  function getArcPct(navShift) {
    if (navShift === 'cleaning') {
      return cleaningItems.length > 0 ? cleaningDoneWeek / cleaningItems.length : 0;
    }
    if (navShift === 'mid-shift') {
      let totalItems = 0, totalDone = 0;
      MID_SUB_SHIFTS.forEach(sub => {
        const entry = getTodayEntry(sub);
        const its   = (venueTemplates[sub] || []).filter(i => isItemActiveToday(i));
        totalItems += its.length;
        totalDone  += (entry?.items || []).filter(i => i.initials).length;
      });
      // No active items today = nothing required = treat as complete
      return totalItems === 0 ? 1 : totalDone / totalItems;
    }
    if (navShift === 'closing') {
      let totalItems = 0, totalDone = 0;
      CLOSING_SUB_SHIFTS.forEach(sub => {
        const entry = getTodayEntry(sub);
        const its   = (venueTemplates[sub] || []).filter(i => isItemActiveToday(i));
        totalItems += its.length;
        totalDone  += (entry?.items || []).filter(i => i.initials).length;
      });
      return totalItems === 0 ? 1 : totalDone / totalItems;
    }
    const entry = getTodayEntry(navShift);
    const its   = (venueTemplates[navShift] || []).filter(i => isItemActiveToday(i));
    // No active items today = nothing required = treat as complete
    if (its.length === 0) return 1;
    const done  = (entry?.items || []).filter(i => i.initials).length;
    return done / its.length;
  }

  function getMidShiftStatus() {
    // Count total active items across both sub-shifts today
    const totalActive = MID_SUB_SHIFTS.reduce((n, sub) =>
      n + (venueTemplates[sub] || []).filter(i => isItemActiveToday(i)).length, 0);
    // No items required today — implicitly complete
    if (totalActive === 0) return 'complete';
    const b = getTodayEntry('mid-shift-bar'), f = getTodayEntry('mid-shift-floor');
    if (!b && !f) return undefined;
    if (b?.status === 'complete' && f?.status === 'complete') return 'complete';
    if (b?.status === 'in_progress' || f?.status === 'in_progress') return 'in_progress';
    return 'partial';
  }

  function getClosingStatus() {
    const totalActive = CLOSING_SUB_SHIFTS.reduce((n, sub) =>
      n + (venueTemplates[sub] || []).filter(i => isItemActiveToday(i)).length, 0);
    if (totalActive === 0) return 'complete';
    const b = getTodayEntry('closing-bar'), f = getTodayEntry('closing-floor');
    if (!b && !f) return undefined;
    if (b?.status === 'complete' && f?.status === 'complete') return 'complete';
    if (b?.status === 'in_progress' || f?.status === 'in_progress') return 'in_progress';
    return 'partial';
  }

  function handleRecurringDone(id)    { setRecurringTasks(prev => prev.filter(t => t.id !== id)); }
  function handleRecurringDismiss(id) { setDismissedRecurring(prev => new Set([...prev, id])); }
  const visibleRecurring = recurringTasks.filter(t => !dismissedRecurring.has(t.id));

  const openingEntry      = getTodayEntry('opening');
  const midBarEntry       = getTodayEntry('mid-shift-bar');
  const midFloorEntry     = getTodayEntry('mid-shift-floor');
  const closingBarEntry   = getTodayEntry('closing-bar');
  const closingFloorEntry = getTodayEntry('closing-floor');

  const midDoneCount      = ((midBarEntry?.items||[]).filter(i=>i.initials).length)     + ((midFloorEntry?.items||[]).filter(i=>i.initials).length);
  const midTotalCount     = ((midBarEntry?.items||[]).length)                           + ((midFloorEntry?.items||[]).length);
  const closingDoneCount  = ((closingBarEntry?.items||[]).filter(i=>i.initials).length) + ((closingFloorEntry?.items||[]).filter(i=>i.initials).length);
  const closingTotalCount = ((closingBarEntry?.items||[]).length)                       + ((closingFloorEntry?.items||[]).length);

  const progressItems = [
    ...(!isKitchen ? [
      { key: 'opening',  label: 'Opening',   sublabel: openingEntry ? `${(openingEntry.items||[]).filter(i=>i.initials).length}/${(openingEntry.items||[]).length} items` : null, status: openingEntry?.status || 'not_started', pct: getArcPct('opening') },
      ...(hasMidShift ? [{ key: 'midshift', label: 'Mid-Shift', sublabel: (midBarEntry||midFloorEntry) ? `${midDoneCount}/${midTotalCount} items` : null, status: getMidShiftStatus() || 'not_started', pct: getArcPct('mid-shift') }] : []),
      { key: 'closing',  label: 'Closing',   sublabel: (closingBarEntry||closingFloorEntry) ? `${closingDoneCount}/${closingTotalCount} items` : null, status: getClosingStatus() || 'not_started', pct: getArcPct('closing') },
    ] : []),
    { key: 'cleaning', label: 'Cleaning', sublabel: cleaningItems.length > 0 ? `${cleaningDoneWeek}/${cleaningItems.length} this week` : null, status: cleaningDoneWeek === cleaningItems.length && cleaningItems.length > 0 ? 'complete' : cleaningDoneWeek > 0 ? 'in_progress' : 'not_started', pct: getArcPct('cleaning') },
  ];

  const venueHistory = localLog.filter(e => e.date !== getToday()).slice(0, 20);

  return (
    <div style={{ padding: '1.25rem', maxWidth: '1060px', margin: '0 auto', background: C.cream, minHeight: '100vh' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.18rem', color: C.charcoal, fontSize: '1.2rem', fontWeight: 700 }}>
            {venue}<span style={{ fontWeight: 400, fontSize: '0.8rem', color: C.mid, marginLeft: '0.55rem', textTransform: 'capitalize' }}>{department}</span>
          </h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: C.mid }}>{formatDate(getToday())}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center' }}>
          <button onClick={() => setShowMaintenance(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.32rem', padding: '0.36rem 0.82rem', borderRadius: '999px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.77rem', cursor: 'pointer', fontWeight: 500 }}>
            <Wrench size={12} /> Maintenance
          </button>
          <button onClick={() => setShowHistory(p => !p)} style={{ padding: '0.36rem 0.82rem', borderRadius: '999px', border: `1px solid ${showHistory ? C.navy : C.border}`, background: showHistory ? C.navy : C.white, color: showHistory ? C.white : C.mid, fontSize: '0.77rem', cursor: 'pointer', fontWeight: showHistory ? 600 : 400 }}>
            {showHistory ? 'Hide History' : 'History'}
          </button>
        </div>
      </div>

      {allVenues && (
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {allVenues.map(v => (
            <button key={v} onClick={() => onVenueChange(v)} style={{ padding: '0.3rem 0.78rem', borderRadius: '999px', border: `1px solid ${venue === v ? C.navy : C.border}`, background: venue === v ? C.navy : C.white, color: venue === v ? C.white : C.mid, fontSize: '0.79rem', cursor: 'pointer', fontWeight: venue === v ? 600 : 400 }}>{v}</button>
          ))}
        </div>
      )}

      {/* Pest check banner — always at top */}
      <PestCheckBanner venue={venue} department={department} currentUser={currentUser} />

      {visibleRecurring.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.1rem' }}>
          {visibleRecurring.map(task => (
            <RecurringAlert key={task.id} task={task} onDone={handleRecurringDone} onDismiss={handleRecurringDismiss} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', overflowX: 'auto', paddingBottom: '0.1rem' }}>
        {sections.map(s => (
          <SectionPill
            key={s} sectionKey={s} isActive={activeSection === s}
            onClick={() => setActiveSection(s)}
            pct={arcSections.includes(s) ? getArcPct(s) : undefined}
            status={
              s === 'mid-shift' ? getMidShiftStatus() :
              s === 'closing'   ? getClosingStatus() :
              s === 'cleaning'  ? (cleaningDoneWeek === cleaningItems.length && cleaningItems.length > 0 ? 'complete' : cleaningDoneWeek > 0 ? 'in_progress' : undefined) :
              CHECKLIST_SHIFTS.includes(s) ? (
                (venueTemplates[s] || []).filter(i => isItemActiveToday(i)).length === 0
                  ? 'complete'
                  : (getTodayEntry(s)?.status || undefined)
              ) : undefined
            }
            showArc={arcSections.includes(s)}
          />
        ))}
      </div>

      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Shift checklists — not cleaning */}
          {CHECKLIST_SHIFTS.includes(activeSection) && activeSection !== 'cleaning' && (
            <ChecklistSection
              shift={activeSection}
              resolvedShift={resolvedShift}
              subShift={activeSection === 'closing' ? closingSubShift : midSubShift}
              onSubShiftChange={activeSection === 'closing' ? setClosingSubShift : setMidSubShift}
              venue={venue}
              templates={venueTemplates}
              logEntry={getTodayEntry(resolvedShift)}
              onSave={handleSave}
              onUnfinalise={handleUnfinalise}
              isAdmin={isAdmin || isManager}
              currentUser={currentUser}
              department={department}
              initials={initials}
              onInitialsChange={handleInitialsChange}
            />
          )}

          {/* Cleaning — new rolling system */}
          {activeSection === 'cleaning' && (
            <CleaningSection
              venue={venue}
              department={department}
              currentUser={currentUser}
            />
          )}

          {activeSection === 'fridgetemps' && <FridgeTempsSection venue={venue} dept={department} currentUser={currentUser} />}
          {activeSection === 'cashup'      && ['bar', 'supervisor', 'manager', 'admin'].includes(userRole) && <CashUpSection venue={venue} currentUser={currentUser} />}

          {showHistory && (
            <div style={{ marginTop: '1.5rem' }}>
              <p style={{ fontSize: '0.68rem', color: C.mid, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em', margin: '0 0 0.65rem' }}>Past Submissions</p>
              {venueHistory.length === 0
                ? <p style={{ color: C.mid, fontSize: '0.84rem' }}>No past submissions.</p>
                : venueHistory.map(entry => (
                  <div key={entry.id} style={{ background: C.white, borderRadius: '9px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '0.82rem 1rem', marginBottom: '0.48rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <span style={{ fontWeight: 600, textTransform: 'capitalize', color: C.charcoal, fontSize: '0.87rem' }}>{entry.shift}</span>
                        <span style={{ color: C.mid, fontSize: '0.77rem', marginLeft: '0.5rem' }}>{formatDate(entry.date)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center' }}>
                        <StatusBadge status={entry.status} />
                        <span style={{ fontSize: '0.71rem', color: C.mid }}>{(entry.items||[]).filter(i=>i.initials).length}/{(entry.items||[]).length}</span>
                      </div>
                    </div>
                    {entry.notes && (
                      <div style={{ fontSize: '0.78rem', color: C.mid, background: C.cream, padding: '0.32rem 0.6rem', borderRadius: '6px', marginTop: '0.38rem' }}>
                        <span style={{ fontWeight: 600 }}>Note: </span>{entry.notes}
                      </div>
                    )}
                  </div>
                ))
              }
            </div>
          )}
        </div>

        <div style={{ width: '200px', flexShrink: 0 }}
          ref={el => {
            if (el) {
              const mq = window.matchMedia('(min-width: 768px)');
              el.style.display = mq.matches ? 'block' : 'none';
              mq.onchange = e => { el.style.display = e.matches ? 'block' : 'none'; };
            }
          }}>
          <ProgressSidebar items={progressItems} />
        </div>
      </div>

      {showMaintenance && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => { if (e.target === e.currentTarget) setShowMaintenance(false); }}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '480px', padding: '1.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Wrench size={16} style={{ color: C.mid }} />
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: C.charcoal }}>Maintenance</span>
              </div>
              <button onClick={() => setShowMaintenance(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={16} /></button>
            </div>
            <MaintenanceSubmitSection venue={venue} currentUser={currentUser} onNavigate={onNavigate} />
          </div>
        </div>
      )}
    </div>
  );
}